import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import {
  DetailedProduct,
  DetailedSolution,
  DownloadQuarterlyResponse,
  DownloadQuarterlyUpdate,
  MailingListRequest,
  ProductItem,
  SolutionMenuItem
} from "../models/products.model";
import { MemoryCacheService } from "./memory-cache.service";
import { SiilaTranslationService } from "./siila-translation.service";

@Injectable({ providedIn: "root" })
export class ProductService {
  lang: string;
  private readonly PRODUCTS_KEY = "menu:products";
  // `:v2` busts the 24h localStorage entries holding the pre-migration LATAM
  // list; without it returning visitors would keep the old records for a day.
  private readonly SOLUTIONS_KEY = "menu:solutions:v2";

  /**
   * LATAM's solution records were migrated to a new set that carries real
   * Spanish copy. The old records — which LATAM shares with BR — are
   * Portuguese-first and never had their `es` fields translated, so LATAM
   * renders English text in the Spanish menu ("Institutional Investors",
   * "Owners & Developers", "Tenants & Occupiers") and links to English pages.
   *
   * Maps old record id -> migrated record id. Only these six moved; LATAM's
   * seventh item (122, "Inversionistas en FIBRAS") has no migrated counterpart
   * and is deliberately absent so it passes through untouched — the MX set's
   * seventh entry is a DIFFERENT record (114, "Inversionistas en REITS").
   */
  private readonly LATAM_MIGRATED_SOLUTION_IDS: { [oldId: number]: number } = {
    101: 108, // Institutional Investors
    102: 109, // Brokerage Firms
    103: 110, // Owners & Developers
    104: 111, // Tenants & Occupiers
    105: 112, // Valuation Advisors
    106: 113 // Service Providers
  };

  /**
   * Country code the migrated LATAM records are currently served under. They
   * live in the MX set, so LATAM composes its menu from two responses. This is
   * a front-end stand-in for the real fix — having `countryCode=LATAM` return
   * the migrated records directly — and should be deleted once that lands.
   */
  private readonly MIGRATED_SOLUTIONS_COUNTRY_CODE = "MX";

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private siilaTranslationService: SiilaTranslationService,
    private memCacheSrv: MemoryCacheService
  ) {
    this.lang = this.siilaTranslationService.getCurrentLanguage();
  }

  /**
   * Get a list of Products add ons
   */
  getMarketAnalytics(): Observable<ProductItem[]> {
    const currentEnv = environment.platformId.toUpperCase();

    const countryCode = currentEnv.toLowerCase();
    const cacheKey = this.PRODUCTS_KEY + countryCode.toLowerCase();

    const cached = this.memCacheSrv.get<ProductItem[]>(cacheKey);
    if (cached) {
      return cached;
    }

    return this.memCacheSrv.set(
      cacheKey,
      this.http.get<ProductItem[]>(
        `${environment.apiURL}/content/product?groupCode=MARKET_ANALYTICS&countryCode=${currentEnv}`
      )
    );
  }

  getProduct(id: number) {
    return this.http.get<DetailedProduct>(`
    ${environment.apiURL}/content/product/${id}`);
  }

  getSolutionList(): Observable<SolutionMenuItem[]> {
    const countryCode =
      environment.platformId == "latam"
        ? "LATAM"
        : environment.platformId.toUpperCase();

    const cacheKey = this.SOLUTIONS_KEY + countryCode.toLowerCase();

    const cached = this.memCacheSrv.get<SolutionMenuItem[]>(cacheKey);
    if (cached) {
      return cached;
    }

    const solutions$ =
      countryCode === "LATAM"
        ? this.fetchLatamSolutionList()
        : this.fetchSolutionList(countryCode);

    return this.memCacheSrv.set(cacheKey, solutions$);
  }

  private fetchSolutionList(
    countryCode: string
  ): Observable<SolutionMenuItem[]> {
    return this.http.get<SolutionMenuItem[]>(
      `${environment.apiURL}/content/solution?countryCode=${countryCode}`
    );
  }

  /**
   * LATAM's menu is composed from two responses: its own list (for ordering and
   * for the records that did NOT migrate) and the set holding the migrated
   * records. See {@link LATAM_MIGRATED_SOLUTION_IDS}.
   *
   * A failure fetching the migrated set degrades to the un-migrated LATAM list
   * rather than taking the whole menu down — the menu then reads as it does
   * today (some English labels) instead of rendering empty in the header,
   * footer and "Who We Serve" at once.
   */
  private fetchLatamSolutionList(): Observable<SolutionMenuItem[]> {
    return forkJoin([
      this.fetchSolutionList("LATAM"),
      this.fetchSolutionList(this.MIGRATED_SOLUTIONS_COUNTRY_CODE).pipe(
        catchError(() => of([] as SolutionMenuItem[]))
      )
    ]).pipe(
      map(([latam, migrated]) => this.applyMigratedSolutions(latam, migrated))
    );
  }

  /**
   * Replace each migrated record in `latam` with its counterpart from
   * `migrated`, preserving LATAM's own `norder` so the menu keeps its current
   * sequence. Records with no mapping — or whose counterpart is missing from
   * the response — pass through unchanged, so a partial response degrades
   * item-by-item instead of dropping menu entries.
   */
  private applyMigratedSolutions(
    latam: SolutionMenuItem[],
    migrated: SolutionMenuItem[]
  ): SolutionMenuItem[] {
    if (!latam || !latam.length || !migrated || !migrated.length) {
      return latam;
    }

    return latam.map(item => {
      const migratedId = this.LATAM_MIGRATED_SOLUTION_IDS[item.id];
      if (!migratedId) {
        return item;
      }
      const replacement = migrated.find(s => s.id === migratedId);
      return replacement ? { ...replacement, norder: item.norder } : item;
    });
  }

  getSolution(id: number) {
    return this.http.get<DetailedSolution>(
      `${environment.apiURL}/content/solution/${id}`
    );
  }

  requestFile(downloadQuarterlyUpdate: DownloadQuarterlyUpdate) {
    return this.http.post<DownloadQuarterlyResponse>(
      `${environment.apiURL}/info/download-quarterly-update`,
      downloadQuarterlyUpdate
    );
  }

  demoRequest(downloadQuarterlyUpdate: Partial<DownloadQuarterlyUpdate>) {
    if (environment.platformId == "latam") {
      downloadQuarterlyUpdate.latam = true;
    }

    if (environment.platformId == "co") {
      downloadQuarterlyUpdate.colombia = true;
    }

    return this.http.post<DownloadQuarterlyResponse>(
      `${environment.apiURL}/info/demoRequest`,
      downloadQuarterlyUpdate
    );
  }

  mailingRequest(downloadQuarterlyUpdate: Partial<DownloadQuarterlyUpdate>) {
    const langLenguaje = this.translate.currentLang.toLowerCase();
    return this.http.post<DownloadQuarterlyResponse>(
      `${environment.apiURL}/mailchimp/${langLenguaje}`,
      downloadQuarterlyUpdate
    );
  }

  requestFileIndex(downloadQuarterlyUpdate: DownloadQuarterlyUpdate) {
    if (environment.platformId == "latam") {
      downloadQuarterlyUpdate.latam = true;
    }

    if (environment.platformId == "co") {
      downloadQuarterlyUpdate.colombia = true;
    }

    downloadQuarterlyUpdate.lang = this.lang;
    return this.http.post<DownloadQuarterlyResponse>(
      `${environment.apiURL}/info/download-index`,
      downloadQuarterlyUpdate
    );
  }

  downloadPdf(filePath: string) {
    const link = document.createElement("a");
    link.href = filePath;
    link.target = "_blank";
    link.click();
  }

  getLangSelector() {
    return this.translate.currentLang === "pt"
      ? "pt-br"
      : this.translate.currentLang;
  }

  infoRequest(downloadQuarterlyUpdate: Partial<DownloadQuarterlyUpdate>) {
    if (environment.platformId == "latam") {
      downloadQuarterlyUpdate.latam = true;
    }

    if (environment.platformId == "co") {
      downloadQuarterlyUpdate.colombia = true;
    }
    return this.http.post<DownloadQuarterlyResponse>(
      `${environment.apiURL}/info/infoRequest `,
      downloadQuarterlyUpdate
    );
  }

  sendEmail(data: MailingListRequest) {
    const params: MailingListRequest = { ...data };
    if (environment.platformId == "latam") params.latam = true;
    if (environment.platformId == "co") params.colombia = true;
    return this.http.post<any>(
      `${environment.apiURL}/info/mailing-list`,
      params
    );
  }
}
